/**
 * Times of day for the event list: each day's events, already in time order, are split
 * into Morning / Afternoon / Evening / No set time, with headings and a "Jump to" bar.
 * The splits match the Morning / Afternoon / Evening filters (12 PM and 5 PM).
 */

export type DayPeriod = "allDay" | "morning" | "afternoon" | "evening";

/**
 * Order of the sections within a day and of the jump bar's buttons: timed periods first,
 * then events with no set time. The server pages events in this order
 * (time ASC NULLS LAST), so a later page only ever adds events below the ones on screen.
 */
export const DAY_PERIOD_ORDER: readonly DayPeriod[] = ["morning", "afternoon", "evening", "allDay"];

export const DAY_PERIODS: Record<DayPeriod, { label: string; shortLabel: string; range: string }> = {
  morning: { label: "Morning", shortLabel: "Morning", range: "Before 12 PM" },
  afternoon: { label: "Afternoon", shortLabel: "Afternoon", range: "12–5 PM" },
  evening: { label: "Evening", shortLabel: "Evening", range: "From 5 PM" },
  // Not "All day": in the filters "All Day" means every time of day, so it read as the default
  allDay: { label: "No set time", shortLabel: "No time", range: "All day or time not listed" },
};

/** A day needs at least this many events before its list gets time-of-day headings. */
export const MIN_EVENTS_FOR_DAY_PERIODS = 4;

/**
 * Minutes after midnight from listing text such as "10:00 AM", "2:30 PM" or "14:00".
 * The event list sorts with this, so the headings must use it too.
 */
export function parseTimeToMinutes(timeStr: string | null | undefined): number | null {
  if (!timeStr || timeStr.trim() === "") return null;

  const normalized = timeStr.trim().toLowerCase();
  const match = normalized.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)?/i);
  if (!match) return null;

  let hours = parseInt(match[1], 10);
  const minutes = match[2] ? parseInt(match[2], 10) : 0;
  const period = match[3];

  // Convert to 24-hour format if AM/PM specified
  if (period) {
    if (period === "pm" && hours !== 12) hours += 12;
    if (period === "am" && hours === 12) hours = 0;
  }

  return hours * 60 + minutes;
}

/** The start time the list sorts by: the server's precomputed minutes, else parsed from the text. */
export function eventSortMinutes(event: { timeSortMinutes?: number | null; time?: string | null }): number | null {
  return event.timeSortMinutes ?? parseTimeToMinutes(event.time);
}

/** Sorts one day's events by start time, events with no set time last (the server's page order). */
export function compareByStartTime(
  a: { timeSortMinutes?: number | null; time?: string | null },
  b: { timeSortMinutes?: number | null; time?: string | null },
): number {
  const timeA = eventSortMinutes(a);
  const timeB = eventSortMinutes(b);
  if (timeA === null && timeB === null) return 0;
  if (timeA === null) return 1;
  if (timeB === null) return -1;
  return timeA - timeB;
}

export function dayPeriodFor(minutes: number | null): DayPeriod {
  if (minutes === null) return "allDay";
  if (minutes < 12 * 60) return "morning";
  if (minutes < 17 * 60) return "afternoon";
  return "evening";
}

export interface DayPeriodSection {
  period: DayPeriod;
  /** Index of the section's first event in the day's list */
  start: number;
  count: number;
}

/** Consecutive runs of events in the same time of day, in list order. */
export function splitByDayPeriod(
  events: Array<{ timeSortMinutes?: number | null; time?: string | null }>,
): DayPeriodSection[] {
  const sections: DayPeriodSection[] = [];
  events.forEach((event, index) => {
    const period = dayPeriodFor(eventSortMinutes(event));
    const last = sections[sections.length - 1];
    if (last && last.period === period) {
      last.count += 1;
    } else {
      sections.push({ period, start: index, count: 1 });
    }
  });
  return sections;
}

/** A missing period can only be in a later page while this is the last loaded day. */
export function canLoadDayPeriod(
  dateString: string,
  loadedDates: readonly string[],
  hasNextPage: boolean,
): boolean {
  return hasNextPage
    && loadedDates.includes(dateString)
    && !loadedDates.some((loadedDate) => loadedDate > dateString);
}

/**
 * What a jump-bar button can do for a day: jump to a loaded section, load more pages to
 * reach it, or report that the day has none.
 *
 * Pages arrive in DAY_PERIOD_ORDER within a day, so once a day's list has reached, say,
 * Evening, a missing Morning or Afternoon can't be on a later page: it is "none" at once
 * rather than after loading the rest of the day.
 */
export type DayPeriodAvailability = "available" | "loadable" | "none";

export function dayPeriodAvailability(
  period: DayPeriod,
  dateString: string,
  periodsByDate: ReadonlyMap<string, readonly DayPeriod[]>,
  loadedDates: readonly string[],
  hasNextPage: boolean,
): DayPeriodAvailability {
  const loadedPeriods = periodsByDate.get(dateString) ?? [];
  if (loadedPeriods.includes(period)) return "available";
  if (!canLoadDayPeriod(dateString, loadedDates, hasNextPage)) return "none";
  const reached = loadedPeriods[loadedPeriods.length - 1];
  if (reached && DAY_PERIOD_ORDER.indexOf(period) < DAY_PERIOD_ORDER.indexOf(reached)) return "none";
  return "loadable";
}

/** Page scroll position that puts a target (viewport `targetTop`) `gap` px below the sticky header and bar. */
export function jumpScrollTop(scrollY: number, targetTop: number, stickyBottom: number, gap: number): number {
  return Math.max(0, Math.round(scrollY + targetTop - stickyBottom - gap));
}

/**
 * Blank space needed below the list so the page can scroll to `destination`: near the end of
 * the loaded list there may not be a screen's worth of events left below the section.
 * `currentSpacer` is the space already there; the result is the total the spacer should be.
 */
export function spacerNeeded(destination: number, scrollHeight: number, viewportHeight: number, currentSpacer: number): number {
  const maxScrollWithoutSpacer = scrollHeight - currentSpacer - viewportHeight;
  return Math.max(0, Math.ceil(destination - maxScrollWithoutSpacer));
}

/**
 * The spacer can shrink by however far the page can still scroll down: that part is below
 * the screen, so removing it never moves what the visitor is looking at.
 */
export function relaxedSpacer(currentSpacer: number, scrollHeight: number, viewportHeight: number, scrollY: number): number {
  const slack = Math.max(0, scrollHeight - viewportHeight - scrollY);
  return Math.max(0, Math.ceil(currentSpacer - slack));
}

export interface DayNames {
  /** "Today" or "Tomorrow"; null for any other day */
  relative: "Today" | "Tomorrow" | null;
  /** "Monday" */
  weekday: string;
  /** "Mon" */
  weekdayShort: string;
  /** "September 28" */
  monthDay: string;
  /** "Sep" */
  monthShort: string;
  /** "28" */
  dayOfMonth: string;
}

/** Ways to name a list day ("YYYY-MM-DD", local time) in headings and the jump bar. */
export function describeDay(dateString: string, now: Date = new Date()): DayNames {
  const [y, m, d] = dateString.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const daysAway = Math.round((date.getTime() - today.getTime()) / 86_400_000);
  return {
    relative: daysAway === 0 ? "Today" : daysAway === 1 ? "Tomorrow" : null,
    weekday: date.toLocaleDateString("en-US", { weekday: "long" }),
    weekdayShort: date.toLocaleDateString("en-US", { weekday: "short" }),
    monthDay: date.toLocaleDateString("en-US", { month: "long", day: "numeric" }),
    monthShort: date.toLocaleDateString("en-US", { month: "short" }),
    dayOfMonth: String(d),
  };
}
